/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect, useState } from 'react'
import Head from 'next/head'
import TopBanner from '@/tabs/home/TopBanner'
import homeApis from '@/apis/home'
import useIsMobile from '@/hooks/useIsMobile'
import dynamic from 'next/dynamic'
import { type GetStaticProps } from 'next'
import { strapiFetch } from '@/lib/actions/strapi'
import RichTextRenderer from '@/components/RichTextEditor'
import FlashSaleProductCard from '@/components/common/flash-sale-product-card'
import storeApis from '@/apis/store'
import { useDispatch, useSelector } from 'react-redux'
import { setBannerColors } from '@/store/features/bannerSlice'
import HomeSkeleton from '@/shimmer/home/HomeSkelton'
import {
  setHomeData,
  setHomeStores,
  setHomeTopBanners,
  setHomeSuperSavings,
  setStoreCategories
} from '@/store/features/homeSlice'
import { type RootState } from '@/store'
import { isGuestUser } from '@/lib/utilities/Utils'

declare global {
  interface Window {
    gtag: (...args: any[]) => void
    dataLayer: any[]
  }
}

export {}

// Lazy loaded components with async/await
const PreviousOrderProducts = dynamic(async () => await import('@/tabs/home/PreviousOrderProducts'), { loading: () => <></> })
const SuperSaving = dynamic(async () => await import('@/tabs/home/SuperSaving'), { loading: () => <></> })
const DiscoverShops = dynamic(async () => await import('@/tabs/home/DiscoverShops'), { loading: () => <></> })
const ShopByCategory = dynamic(async () => await import('@/tabs/home/ShopByCategory'), { loading: () => <></> })
const FlashCarousel = dynamic(async () => await import('@/components/FlashProductsList'), {
  ssr: false,
  loading: () => <></>
})
const OrderTracking = dynamic(async () => await import('@/components/common/order-tracking'), {
  ssr: false,
  loading: () => <></>
})

export interface StrapiData {
  data: {
    home: any
  }
}

export default function Home ({ data }: StrapiData): JSX.Element {
  const {
    homeData,
    homeTopBanners,
    homeStores,
    homeSuperSavings
  } = useSelector((state: any) => state.home)

  const [loadingState, setLoadingState] = useState('loading')
  const [homePageData, setHomePageData] = useState<any>(homeData ?? null)
  const [stores, setStores] = useState<[]>(homeStores ?? [])
  const [superSavingsData, setSuperSavingsData] = useState<any>(homeSuperSavings ?? null)
  const [banners, setBanners] = useState<any>(homeTopBanners ?? [])
  const guestReady = useSelector((state: RootState) => state?.commonData?.isGuestReady)

  const isMobile = useIsMobile()
  const dispatch = useDispatch()

  const loadHomeData = async () => {
    let homeBanners: never[] = []

    const homePromise = homeApis.getHome().then((res) => {
      if (res?.status === 200) {
        setLoadingState('success')
        setHomePageData(res?.data?.data)
        dispatch(setHomeData(res?.data?.data))
        dispatch(setStoreCategories(res?.data?.data?.storeCategories ?? []))
        homeBanners = res?.data?.data?.banners ?? []
      }
    }).catch(() => {
      setLoadingState('error')
    })

    const storesPromise = storeApis.getStores().then((res) => {
      setStores(res?.data?.data?.stores)
      dispatch(setHomeStores(res?.data?.data?.stores))
    }).catch(() => {
    })

    const unifiedBannerPromise = homeApis.getUnifiedBanner('HOME').then((res) => {
      if (res?.data?.success === true && res.data.data.length > 0) {
        setBanners([...res.data.data])
        dispatch(setHomeTopBanners([...res.data.data]))
        dispatch(setBannerColors({
          bgColor: res?.data.data[0].topBarColour ?? '',
          textColor: res?.data?.data[0].topBarTextColour ?? ''
        }))
        return true
      }
      return false
    }).catch(() => false)

    const superSavingsPromise = homeApis.getSuperSavings({ isFreeSample: true }).then((res) => {
      setSuperSavingsData(res?.data?.data)
      dispatch(setHomeSuperSavings(res?.data?.data))
    }).catch(() => {
    })

    const [, , unifiedSuccess] = await Promise.all([homePromise, storesPromise, unifiedBannerPromise, superSavingsPromise])
    if (!unifiedSuccess && homeBanners.length > 0) {
      setBanners([...homeBanners])
      dispatch(setHomeTopBanners([...homeBanners]))
    }
  }

  useEffect(() => {
    if (isGuestUser() && !guestReady) return
    void loadHomeData()
  }, [guestReady])

  useEffect(() => {
    window.dataLayer = Array.isArray(window.dataLayer)
      ? window.dataLayer
      : []

    window.dataLayer.push({
      event: 'homescreen_viewed',
      page_url: window.location.href
    })

    ;(window as any).gtag?.('event', 'homescreen_viewed', {
      page_location: window.location.href
    })
  }, [])

  return (
    <React.Fragment>
      {/* Set page title and meta description */}
      <Head>
        <title>
          Fruits, Vegetables & Grocery Delivery in Mumbai – LoveLocal
        </title>
        <meta
          name="description"
          content="Shop fresh fruits, veggies, meat, fish & groceries from Mumbai’s trusted shops. LoveLocal delivers fast with quality you trust."
        />
        <link
          rel="canonical"
          href={`https://${window.location.host}/`}
        />

        {/* Home Page basic BreadCrumb Details */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Organization',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Goregaon East, Mumbai, Maharashtra',
              postalCode: '400063',
              streetAddress: 'WeWork, 1st Floor, Commerz II, Oberoi Garden City'
            },
            email: 'cs@lovelocal.in',
            faxNumber: '+91 8448448906',
            name: 'LoveLocal',
            telephone: '+91 8448448906'
          })}
        </script>
      </Head>
      {!isMobile &&
        <OrderTracking
        />
      }
      {/* Render the home page content */}
      <div className={`container mx-${isMobile ? '0' : 'auto'}`}>
        <TopBanner
          banners={banners}
          onBannerChange={(banner: any) => {
            dispatch(setBannerColors({
              bgColor: banner?.topBarColour ?? '',
              textColor: banner?.topBarTextColour ?? ''
            }))
          }}
          isLoading={loadingState === 'loading'}
        />
        {(() => {
          let showcase = null
          if (loadingState === 'loading' && homePageData === null) {
            showcase = <HomeSkeleton/>
          } else if (loadingState === 'error' && homePageData === null) {
            showcase = (
              <div className='flex justify-center text-red-500 my-2'>
                <h1>Oops something went wrong. Retry!</h1>
              </div>
            )
          } else if (homePageData !== null) {
            showcase = (
              <div>
                <FlashCarousel
                  items={homePageData?.flashSaleProducts}
                  renderItem={(item: any, index: number) => (
                    <FlashSaleProductCard key={index} product={item}/>
                  )}
                />
                <PreviousOrderProducts
                  isRepeatOrderStore={homePageData?.repeatOrderStores?.length === 1}
                  repeatOrderProducts={homePageData?.repeatOrderProducts}
                  repeatOrderStores={homePageData?.repeatOrderStores?.[0]}
                />
                <SuperSaving
                  superSavings={superSavingsData?.superSavings?.filter((offer: any) => offer.type !== 'REFER_AND_EARN' && offer.type !== 'SCRATCH_CARD')}
                />
                <DiscoverShops
                  stores={stores}
                />
                <ShopByCategory
                  storeCategories={homePageData?.storeCategories}
                />
                {/* <StaticContent /> */}
                <div className='home-strapi'>
                  <RichTextRenderer content={data?.home}/>
                </div>
              </div>
            )
          }
          return showcase
        })()}
      </div>

    </React.Fragment>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  try {
    const data = await strapiFetch<{ data: any }>({
      path: '/home-page',
      params: {
        populate: '*',
        'pagination[pageSize]': 100
      }
    })

    return {
      props: {
        data: data.data ?? []
      },
      revalidate: 60
    }
  } catch (err) {
    return {
      props: { posts: [] },
      revalidate: 60
    }
  }
}
